import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  Button,
  Combobox,
  DialogFooter,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@accure/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, RefreshCw } from 'lucide-react';
import { z } from 'zod';

import { Tooltip } from '@/components/ui/tooltip';
import { LinkTaskMutation } from '@/project-management/mutations/link-task.mutation';
import { TasksQuery } from '@/project-management/queries/tasks.query';
import { cls } from '@/utils/cls';

export type LinkTaskFormProps = {
  siteId: string;
  workstreamId: string;
  onClose: () => void;
};

const FormSchema = z.object({ taskId: z.string() });
type Form = z.infer<typeof FormSchema>;

export function LinkTaskForm({
  siteId,
  workstreamId,
  onClose,
}: LinkTaskFormProps) {
  const linkTask = LinkTaskMutation.useMutation();
  const tasksQuery = TasksQuery.useQuery({ siteId });
  const form = useForm<Form>({
    resolver: zodResolver(FormSchema),
  });

  async function handleSubmit(data: Form) {
    await toast.promise(
      linkTask.mutateAsync({
        siteId,
        workstreamId,
        taskId: data.taskId,
      }),
      {
        pending: 'Linking task...',
        success: 'Task linked successfully.',
        error: 'Failed to link task. Please try again.',
      },
    );
    onClose();
  }

  if (tasksQuery.isError) {
    throw tasksQuery.error;
  }

  return (
    <>
      <Form {...form}>
        <form
          id="add-workstream-link-form"
          onSubmit={form.handleSubmit(handleSubmit)}
          className="space-y-4"
        >
          <FormField
            control={form.control}
            name="taskId"
            render={({ field }) => {
              return (
                <FormItem className="gap-2">
                  <FormLabel>Select a Task</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Combobox
                        className="w-full"
                        options={tasksQuery.data?.tasks.map((task) => ({
                          value: task.id,
                          label: task.title,
                        }))}
                        isLoading={tasksQuery.isLoading}
                        onSelect={field.onChange}
                        labels={['a task', 'tasks']}
                      />
                      <div className="absolute right-2 top-1/2 size-6 -translate-y-1/2 p-0">
                        <Tooltip delay content="Refresh the list of tasks">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => tasksQuery.refetch()}
                            disabled={tasksQuery.isRefetching}
                            className="size-6"
                          >
                            <RefreshCw
                              className={cls('size-3.5', {
                                'animate-spin': tasksQuery.isRefetching,
                              })}
                            />
                          </Button>
                        </Tooltip>
                      </div>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        </form>
      </Form>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="add-workstream-link-form"
          disabled={linkTask.isLoading}
        >
          {linkTask.isLoading ? (
            <>
              <Loader2 className="size-3.5 animate-spin" />
              Adding...
            </>
          ) : (
            <>Add Task</>
          )}
        </Button>
      </DialogFooter>
    </>
  );
}
